import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { Button } from '../../components/ui';
import api from '../../services/api';
import { uploadCatalogItemFiles, deleteCatalogItemFile, updateCatalogItem } from '../../services/mutations';
import { cn } from '../../lib/utils';

interface ResizeImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: number;
  fileId: number;
  fileName: string | null;
  fileSize: number | null;
  wasPrimary: boolean;
  onSuccess: () => void;
}

const DEFAULT_MAX_WIDTH = 800;
const DEFAULT_QUALITY = 0.85;

/** Resize image via canvas, then upload as new file and replace the old one (delete old, set primary if needed). */
export const ResizeImageModal: React.FC<ResizeImageModalProps> = ({
  isOpen,
  onClose,
  itemId,
  fileId,
  fileName,
  fileSize,
  wasPrimary,
  onSuccess,
}) => {
  const { colors } = useTheme();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maxWidth, setMaxWidth] = useState(DEFAULT_MAX_WIDTH);
  const [quality, setQuality] = useState(DEFAULT_QUALITY);
  const [preview, setPreview] = useState<{ dataUrl: string; width: number; height: number; size: number } | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!isOpen || !itemId || !fileId) return;
    setError(null);
    setPreview(null);
    setMaxWidth(DEFAULT_MAX_WIDTH);
    setQuality(DEFAULT_QUALITY);
    let revoked = false;
    setLoading(true);
    api
      .get(`/catalog-items/${itemId}/files/${fileId}`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        const blob = res.data as Blob;
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(() => {
        if (!revoked) setError('Failed to load image');
      })
      .finally(() => {
        if (!revoked) setLoading(false);
      });
    return () => {
      revoked = true;
      setBlobUrl((u) => {
        if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
        return null;
      });
    };
  }, [isOpen, itemId, fileId]);

  const updatePreview = useCallback(() => {
    if (!blobUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const scale = maxWidth > 0 && img.width > maxWidth ? maxWidth / img.width : 1;
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (b) => {
          if (!b) return;
          setPreview((prev) => {
            if (prev?.dataUrl && prev.dataUrl.startsWith('blob:')) URL.revokeObjectURL(prev.dataUrl);
            return { dataUrl: URL.createObjectURL(b), width: w, height: h, size: b.size };
          });
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => setError('Invalid image');
    img.src = blobUrl;
  }, [blobUrl, maxWidth, quality]);

  useEffect(() => {
    if (!blobUrl) return;
    updatePreview();
  }, [blobUrl, maxWidth, quality, updatePreview]);

  const handleApply = async () => {
    if (!preview || !blobUrl) return;
    setApplying(true);
    setError(null);
    try {
      const res = await fetch(preview.dataUrl);
      const blob = await res.blob();
      const file = new File([blob], (fileName || 'image').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      const updated = await uploadCatalogItemFiles(itemId, [file]);
      const attachments = (updated as { FileAttachment?: Array<{ id: number }> })?.FileAttachment ?? [];
      const newFileId = attachments.length ? Math.max(...attachments.map((f) => f.id)) : null;
      if (wasPrimary && newFileId != null) {
        await updateCatalogItem(itemId, { primaryFileAttachmentId: newFileId });
      }
      await deleteCatalogItemFile(itemId, fileId);
      onSuccess();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to resize and replace');
    } finally {
      setApplying(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader>
        <ModalTitle>Resize image</ModalTitle>
        <button type="button" onClick={onClose} className="p-1 rounded hover:bg-black/10" aria-label="Close">
          <span className="sr-only">Close</span>
        </button>
      </ModalHeader>
      <ModalContent>
        <p className={cn('text-sm mb-3', colors.textMuted)}>
          Shrink the image to reduce file size. The image will be converted to JPEG and replace the current file.
        </p>
        {loading && <p className={cn('text-sm', colors.textMuted)}>Loading image…</p>}
        {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}
        {blobUrl && !loading && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center">
              <label className={cn('text-sm font-medium', colors.text)}>
                Max width (px):
                <input
                  type="number"
                  min={100}
                  max={4000}
                  value={maxWidth}
                  onChange={(e) => setMaxWidth(Number(e.target.value) || DEFAULT_MAX_WIDTH)}
                  className={cn('ml-2 w-24 rounded border px-2 py-1 text-sm', colors.border, colors.surface)}
                />
              </label>
              <label className={cn('text-sm font-medium', colors.text)}>
                Quality (0.1–1):
                <input
                  type="number"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={quality}
                  onChange={(e) => setQuality(Number(e.target.value) || DEFAULT_QUALITY)}
                  className={cn('ml-2 w-20 rounded border px-2 py-1 text-sm', colors.border, colors.surface)}
                />
              </label>
            </div>
            {preview && (
              <div className={cn('rounded-lg border p-3', colors.border, colors.surface)}>
                <p className={cn('text-xs mb-2', colors.textMuted)}>
                  Preview: {preview.width}×{preview.height} — {formatSize(preview.size)}
                  {fileSize != null && (
                    <span className="ml-2">
                      (was {formatSize(fileSize)})
                    </span>
                  )}
                </p>
                <img
                  src={preview.dataUrl}
                  alt="Preview"
                  className="max-h-48 w-auto rounded border border-gray-200 dark:border-gray-600"
                />
              </div>
            )}
          </div>
        )}
      </ModalContent>
      <ModalFooter className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="default"
          onClick={handleApply}
          disabled={!preview || applying}
          loading={applying}
        >
          Resize & replace
        </Button>
      </ModalFooter>
    </Modal>
  );
};
